import cx from 'classnames';
import {map} from 'lodash';
import moment from 'moment';
import {useCallback, useContext, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, Tooltip} from 'components';
import {ACCOUNT_PLAN_BANNER_PATH} from 'constants/images';
import {UserContext} from 'context';
import {useObservable} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {accountPlanStore} from 'services';

import './styles.scss';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.ACCOUNT_PLAN_UPGRADE_BANNER;
const TOOLTIP_T_KEYS = T_KEYS.TOOLTIP_INFO;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON.ALL;

const enum LimitKey {
    InSessionMinutesLeftForToday = 'inSessionMinutesLeftForToday',
    MaxSessionDuration = 'maxSessionDuration',
    MaxUsers = 'maxUsers'
}

const limitTKey = {
    [LimitKey.InSessionMinutesLeftForToday]: 'IN_SESSION_MINUTES_LEFT_FOR_TODAY',
    [LimitKey.MaxSessionDuration]: 'MAX_SESSION_DURATION',
    [LimitKey.MaxUsers]: 'MAX_USERS'
};

type Props = {
    upgradeUrl: string;
    className?: string;
}

export const FreePlanBanner: React.FC<Props> = ({upgradeUrl, className}) => {
    const {t} = useTranslation();
    const {limits, usage} = useObservable(accountPlanStore);
    const {settings: {common: {timeZone}}} = useContext(UserContext);

    const [currentMoment, setCurrentMoment] = useState<moment.Moment>();
    const getNow = useCallback(() => moment().tz(timeZone), [timeZone]);

    const nextDay = useMemo(() => moment(currentMoment).add(1, 'd').startOf('day'), [currentMoment]);
    const hoursDiff = useMemo(() => moment(nextDay).diff(currentMoment, 'hours'), [currentMoment, nextDay]);
    const minutesDiff = useMemo(() => nextDay.diff(currentMoment, 'minutes') % 60, [currentMoment, nextDay]);

    const inSessionMinutesLeftForToday = useMemo(() => {
        const diff = Math.ceil(limits?.maxInSessionMinutesPerDay - usage?.inSessionMinutes);
        return Math.max(diff, 0);
    }, [usage, limits]);

    const renderInSessionMinutesLeftForTodayTooltip = useCallback(() => {
        const hoursLeftMessage = hoursDiff > 0 ? `${hoursDiff} ${t(COMMON_T_KEYS.HOURS_SHORT)}` : '';
        const minutesLeftMessage = minutesDiff > 0 ? `${minutesDiff} ${t(COMMON_T_KEYS.MINUTES_SHORT)}` : '';

        const tooltipProps = {
            header: `${t(TOOLTIP_T_KEYS.MAXIMUM)} ${limits?.maxInSessionMinutesPerDay} ${t(COMMON_T_KEYS.MINUTES_SHORT)}`,
            message: `${t(TOOLTIP_T_KEYS.RESET_IN)} ${hoursLeftMessage} ${minutesLeftMessage}`
        };
        return (
            <Tooltip
                {...tooltipProps}
                color='light'
                className='tw-align-middle'
                onOpen={() => setCurrentMoment(getNow())}
            >
                <Icon
                    name='info'
                    button
                    color='light'
                    className='tw-ml-1'
                    size='sm'
                />
            </Tooltip>
        );
    }, [getNow, hoursDiff, minutesDiff, limits, t]);

    return (
        <div className={cx('bg-account-plan-banner tw-text-light tw-rounded-md', className)}>
            <img
                src={ACCOUNT_PLAN_BANNER_PATH}
                alt='Person'
                className='-md:tw-hidden tw-absolute tw-right-0 tw-bottom-0 tw-max-h-full'
            />
            <span className='tw-ts-h3-bold 2xl:tw-ts-h2-bold'>{`${t(T_KEYS.TITLE)} ${t(T_KEYS.NAMES.FREE)}`}</span>
            <div className='tw-relative tw-mt-7 sm:tw-w-3/4 md:tw-w-2/3 xl:tw-w-3/4'>
                <div className='md:tw-flex tw-justify-between'>
                    {map(limitTKey, (value, key) => (
                        <div key={key} className='tw-flex tw-items-center'>
                            <div className='tw-ts-h2-bold lg:tw-ts-h1-bold -md:tw-w-11 tw-mr-3'>
                                {key === LimitKey.InSessionMinutesLeftForToday ? inSessionMinutesLeftForToday : limits[key]}
                            </div>
                            <div className='lg:tw-ts-h5 2xl:tw-ts-h4 sm:tw-mr-3'>
                                {t(T_KEYS.PROPERTIES[value])}
                                {key === LimitKey.InSessionMinutesLeftForToday &&
                                    renderInSessionMinutesLeftForTodayTooltip()}
                            </div>
                        </div>
                    ))}
                </div>
                {upgradeUrl &&
                    <div className='tw-mt-6 -md:tw-mt-5'>
                        <a
                            href={upgradeUrl}
                            className='tw-ts-h5-bold 2xl:tw-ts-h3-bold'
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            <div className='tw-flex tw-items-center'>
                                <span>{t(T_KEYS.BUTTONS.UPGRADE)}</span>
                                <Icon name='arrow-right' color='light' className='tw-ml-2' />
                            </div>
                        </a>
                    </div>}
            </div>
        </div>
    );
};
